import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { DatePicker, Form, Popconfirm, Table } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { QuestionCircleOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FieldData } from 'rc-field-form/es/interface';
import { appConfig } from '../../../settings';
import { IIncludeAssignedCompanyItem, IShowComponent, RareFormTab } from '../../../interfaces';
import { useCompaniesSelect, useIncludeAssignedCompaniesActive, useUiIsSpinnerFound } from '../../../hooks';
import {
    addIncludedList,
    addRateChangedFormTab,
    apiMessagesDeleteMessageFormFieldName,
    apiMessagesGetFormRules,
    apiMessagesGetFormRulesFieldBlank,
    deleteRateCompanyAssignedV2,
    deleteRateNewCompanyAssignedV2,
    removeRateChangedFormTab,
    saveRateCompanyAssignedV2,
} from '../../../middleware';
import { disablePreviousDates, globalHexId, isArrayHasNewId, isNewId } from '../../../instruments';
import { tB, tF, tNM } from '../../../translate';
import {
    CardLayout,
    Catcher,
    CustomButton,
    EditButton,
    FormSelect,
    RemoveButton,
    SkyPaginationTotal,
    cardLayoutOptions,
} from '../../../Components';
import IconStyles from '../../IconComponents/m_styles.less';
import ViewStyles from '../../styles/m_viewStyles.less';

export const EditRateCompaniesAssigned: React.FC<IShowComponent> = observer(({ loadingTypes }: IShowComponent) => {
    const [editId, setEditId] = useState<string | undefined>();
    const [isValuesChange, setIsValuesChange] = useState<boolean>(false);
    const [form] = Form.useForm();
    const isLoading = !!loadingTypes && !!loadingTypes.length && useUiIsSpinnerFound(loadingTypes);
    const pageSize = appConfig.pageDefaultParams.includeViewPageSize;
    const list = useIncludeAssignedCompaniesActive();
    const countriesSelect = useCompaniesSelect;

    const isHasNew = isArrayHasNewId(list);

    useEffect(() => {
        const currentItem = list.find((i: IIncludeAssignedCompanyItem) => i.id === editId);
        form.setFieldValue('companyId', currentItem?.companyId);
        form.setFieldValue('startDate', currentItem?.startDate);
        if (!editId) {
            removeRateChangedFormTab(RareFormTab.COMPANIES);
        }
    }, [editId]);

    const checkValid = (): void => {
        form.validateFields();
    };

    const addCompany = () => {
        const newACompany: IIncludeAssignedCompanyItem = { id: globalHexId.getId(), startDate: dayjs() };
        addIncludedList('assignedCompanies', newACompany);
        setEditId(newACompany.id);
    };

    const startEditCompany = (id: string) => {
        setEditId(id);
        setIsValuesChange(false);
        removeRateChangedFormTab(RareFormTab.COMPANIES);
    };
    const deleteCompany = async (id: string) => {
        await deleteRateCompanyAssignedV2(id);
    };
    const saveCompany = async (id: string) => {
        const values = form.getFieldsValue();
        const isSaved = await saveRateCompanyAssignedV2(id, values);
        if (isSaved) {
            setEditId(undefined);
        } else {
            checkValid();
        }
    };
    const canselEditCompany = (id: string) => {
        if (isNewId(id)) {
            deleteRateNewCompanyAssignedV2(id);
        }
        setEditId(undefined);
    };

    const onValuesChange = (changedFields: FieldData[]) => {
        Object.keys(changedFields).forEach((key) => {
            apiMessagesDeleteMessageFormFieldName(key);
        });
        setIsValuesChange(true);
        addRateChangedFormTab(RareFormTab.COMPANIES);
    };

    const tableColumns = [
        {
            title: tF('Company'),
            dataIndex: 'companyLink',
            ellipsis: true,
        },
        {
            title: tF('Start Date'),
            dataIndex: 'startDate',
            width: 180,
        },
        {
            title: '',
            dataIndex: 'toolBar',
            className: ViewStyles.columnToolbar,
        },
    ];
    const tableData = list.map((r: IIncludeAssignedCompanyItem) => {
        const { id, isActive: isActiveItem, isDeletable } = r;
        const isActive = isActiveItem || isNewId(id);
        return {
            key: id,
            companyLink:
                editId === r.id ? (
                    <div className={ViewStyles.tableFieldMarginTop}>
                        <FormSelect
                            disabled={!isActive || isLoading}
                            fieldName={'companyId'}
                            isGetPopupContainerEnabled={false}
                            isNotClear
                            isShortForm
                            rules={apiMessagesGetFormRules}
                            // rules={apiMessagesGetFormRulesFieldBlank()}
                            useSelectHook={countriesSelect}
                        />
                    </div>
                ) : (
                    r.company
                ),
            startDate:
                editId === r.id ? (
                    <div className={ViewStyles.tableFieldMarginTop}>
                        <Form.Item name={'startDate'} rules={apiMessagesGetFormRulesFieldBlank()}>
                            <DatePicker disabledDate={disablePreviousDates} disabled={!isActive || isLoading} />
                        </Form.Item>
                    </div>
                ) : r.startDate?.isValid() ? (
                    dayjs(r.startDate).format(appConfig.formatDate)
                ) : undefined,
            toolBar:
                editId === r.id ? (
                    <>
                        <CustomButton
                            title={'Save'}
                            type={'primary'}
                            icon={<SaveOutlined />}
                            disabled={isLoading || !isValuesChange}
                            onClick={() => saveCompany(r.id)}
                        />
                        <CustomButton
                            title={'Cancel'}
                            type={'primary'}
                            isGhost
                            icon={<StopOutlined />}
                            onClick={() => canselEditCompany(r.id)}
                            disabled={isLoading}
                        />
                    </>
                ) : (
                    <>
                        <EditButton
                            isGhost
                            disabled={!isActive || isLoading}
                            onClick={(): void => {
                                startEditCompany(r.id);
                            }}
                        />
                        <Popconfirm
                            cancelText={tB('Cancel')}
                            okText={tB('Delete')}
                            onConfirm={() => deleteCompany(r.id)}
                            placement="bottom"
                            title={tNM('Confirm deletion.')}
                            icon={<QuestionCircleOutlined className={IconStyles.canceled} />}
                        >
                            <RemoveButton disabled={!isDeletable || isLoading} isGhost />
                        </Popconfirm>
                    </>
                ),
        };
    });

    const pagination: false | TablePaginationConfig =
        pageSize > list.length
            ? false
            : {
                  total: list.length,
                  pageSize: pageSize,
                  className: 'customPagination',
                  showSizeChanger: false,
                  size: 'default',
                  showTotal: SkyPaginationTotal,
              };

    return (
        <Catcher>
            <CardLayout
                columnOption={cardLayoutOptions.cardTableFull}
                count={list.length}
                idSpinners={loadingTypes}
                isTable
                onClickAddButton={isHasNew ? undefined : addCompany}
                title={'Companies Assignments'}
            >
                <Form
                    form={form}
                    name="companiesAssignments"
                    initialValues={{}}
                    scrollToFirstError={true}
                    layout={'vertical'}
                    className={'formStyle'}
                    onValuesChange={onValuesChange}
                >
                    <Table
                        className={'table-theme sub-table'}
                        columns={tableColumns}
                        dataSource={tableData}
                        loading={isLoading}
                        pagination={pagination}
                        size={'middle'}
                    />
                </Form>
            </CardLayout>
        </Catcher>
    );
});
